import { api } from '../api/client';
import { connectEvents } from '../api/events';
import type { Job } from '../api/types';
import { upsertLibrary, loadLibraries } from './libraries.svelte';
import { showToast } from './toast.svelte';
import { t } from '../i18n';

export const jobsState = $state<{ items: Job[]; activityOpen: boolean; eventsConnected: boolean }>({
  items: [], activityOpen: false, eventsConnected: false,
});

export const runningCount = () => jobsState.items.filter((j) => j.state === 'running' || j.state === 'queued').length;

export async function loadJobs() {
  jobsState.items = await api.jobs();
}

/** Jobs started from this tab: their outcome is announced (and downloads start by themselves). */
const watched = new Set<string>();

export function watchJob(job: Job) {
  watched.add(job.id);
  upsertJob(job);
}

function finished(job: Job) {
  if (!watched.has(job.id)) return;
  if (job.state === 'done') {
    watched.delete(job.id);
    if (job.downloadUrl && job.kind === 'download') {
      const a = document.createElement('a');
      a.href = job.downloadUrl;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    showToast(t(`jobs.done.${job.kind}`, { title: job.title }));
  } else if (job.state === 'failed') {
    watched.delete(job.id);
    showToast(`${job.title}: ${job.message || t('common.error')}`, 'error');
  } else if (job.state === 'cancelled') {
    watched.delete(job.id);
  }
}

function upsertJob(job: Job) {
  finished(job);
  const i = jobsState.items.findIndex((j) => j.id === job.id);
  if (i >= 0) jobsState.items[i] = job;
  else jobsState.items.unshift(job);
}

let unsubscribe: (() => void) | null = null;

export function startJobEvents() {
  if (unsubscribe) return;
  let everOpen = false;
  unsubscribe = connectEvents({
    onJob: upsertJob,
    onLibrary: upsertLibrary,
    onOpen: () => {
      jobsState.eventsConnected = true;
      // after a reconnect, catch up with what happened while the stream was down
      if (everOpen) {
        loadLibraries().catch(() => {});
        loadJobs().catch(() => {});
      }
      everOpen = true;
    },
    onError: () => { jobsState.eventsConnected = false; },
  });
}

export function stopJobEvents() {
  unsubscribe?.();
  unsubscribe = null;
}

export async function cancelJob(id: string) {
  const job = await api.cancelJob(id);
  upsertJob(job);
}

export async function retryJob(id: string) {
  const job = await api.retryJob(id);
  watched.add(job.id);
  upsertJob(job);
}

export async function clearFinished() {
  await api.clearFinishedJobs();
  jobsState.items = jobsState.items.filter((j) => j.state === 'running' || j.state === 'queued');
}

export function toggleActivity(open?: boolean) {
  jobsState.activityOpen = open ?? !jobsState.activityOpen;
}
